// Unified content model for the marketing site (Solution polish × BMSIT events domain).
import {
  CalendarCheck, ShieldCheck, Megaphone, QrCode, Award, Users,
  BookOpen, ClipboardList, MapPin,
  Lock, KeyRound, Server, Globe, GitBranch, Clock, BarChart3, Building2,
} from "lucide-react";

export const NAV_LINKS = [
  { to: "/features",     label: "Features" },
  { to: "/how-it-works", label: "How it works" },
  { to: "/products",     label: "Products" },
  { to: "/security",     label: "Security" },
  { to: "/events",       label: "Public events" },
  { to: "/contact",      label: "Contact" },
];

// Audience cards on hero
export const AUDIENCES = [
  {
    key: "faculty",
    icon: BookOpen,
    title: "Faculty & clubs",
    tagline: "Book a hall in minutes.",
    accent: "from-cobalt-500 to-cobalt-700",
    perks: ["Live availability", "Multi-day", "Permission letters"],
  },
  {
    key: "incharge",
    icon: ShieldCheck,
    title: "Hall incharges",
    tagline: "Approve and manage your auditorium.",
    accent: "from-cyanx to-cobalt-500",
    perks: ["Your halls only", "Approvals", "Notes"],
  },
  {
    key: "organiser",
    icon: Megaphone,
    title: "Organisers",
    tagline: "Run the whole event end to end.",
    accent: "from-chartreuse to-cyanx",
    perks: ["Registration", "QR check-in", "Certificates"],
  },
];

// Capabilities grid
export const FEATURES = [
  { icon: CalendarCheck, title: "Live availability",
    desc: "Check any hall by date and slot. Overlapping times are blocked automatically, so two events never land on the same stage.",
    detail: "Slot intervals are compared server-side, so overlapping requests like 8–11 AM and 9 AM–12 PM can't both be booked — and back-to-back slots aren't penalised. Multi-day series are handled in a single request." },
  { icon: ShieldCheck,   title: "Incharge approvals",
    desc: "Every request routes to that auditorium's incharge. They approve or decline only the bookings for their own halls.",
    detail: "Each hall carries an assigned incharge. Bookings and broadcasts are scoped server-side, with audit logs kept in the database for every action." },
  { icon: Megaphone,     title: "Event pages & registration",
    desc: "Publish an event, open registration with seat caps and waitlists, and share a public page anyone can sign up on.",
    detail: "Standalone events and event-from-booking both work. Each attendee gets a confirmation email with a tokenised link, plus a QR ticket they can present at the gate." },
  { icon: QrCode,        title: "QR check-in",
    desc: "Every ticket carries a QR code. Scan attendees in at the gate and watch attendance update live.",
    detail: "The scanner reads registration + token off the QR and validates against the database. Duplicate scans are rejected. The camera module ships with permission handling and fallbacks for blocked environments." },
  { icon: Award,         title: "Certificates",
    desc: "Issue verifiable participation certificates in one click, each with its own public verification link.",
    detail: "Certificates are tokenised (30-day validity by default) and rendered through a public /verify endpoint — anyone with the link can confirm participation in seconds." },
  { icon: Users,         title: "Coordinators & roles",
    desc: "Add coordinators with scoped access — registrations, check-in, or feedback — without handing over the whole event.",
    detail: "An event team model lets the event owner invite coordinators by email and toggle permissions per area. Authorization is enforced both in the UI and in the Worker, so coordinators never see more than they should." },
];

// Onboarding steps
export const STEPS = [
  { n: "01", icon: ClipboardList, title: "Request a hall",
    desc: "Pick a date and slot. Overlaps are caught before you submit, and multi-day series are handled in one go." },
  { n: "02", icon: ShieldCheck,   title: "Incharge approves",
    desc: "The auditorium's incharge reviews and confirms. Everyone gets an email as the status changes." },
  { n: "03", icon: Megaphone,     title: "Publish & register",
    desc: "Turn the booking into an event page, open registration, and share the link with your audience." },
  { n: "04", icon: QrCode,        title: "Check in & certify",
    desc: "Scan QR tickets at the gate, then issue certificates to everyone who attended." },
];

export const HALLS = [
  { code: "BSN",  name: "BSN Auditorium" },
  { code: "APJ",  name: "APJ Hall" },
  { code: "A226", name: "A-226 Seminar Hall" },
  { code: "A116", name: "A-116 Seminar Hall" },
  { code: "AMPH", name: "Amphitheatre" },
  { code: "EST",  name: "EST Hall" },
  { code: "LBA",  name: "LBA Hall" },
];

export const STATS = [
  ["7", "halls, one platform"],
  ["QR", "check-in at the gate"],
  ["Auto", "certificates"],
];

// Solution-style product family — Arventiq Labs suite
export const PRODUCTS = [
  { name: "FortifyHub", tag: "Secure exams", url: "https://fortifyhub.net",
    desc: "India's secure online exam platform — 7 question types, 12+ Indian languages, proctoring, and live coding." },
  { name: "LMS Platform", tag: "Learning", url: "https://lms.fortifyhub.net",
    desc: "Courses, content delivery, and learner progress — tightly integrated with FortifyHub." },
  { name: "Placement Portal", tag: "Campus placements", url: "https://bmsit-placement.fortifyhub.net",
    desc: "Drive scheduling, eligibility gates, QR check-in, and placement analytics for institutions." },
  { name: "NexusGuard", tag: "Lab security", url: "https://arventiqlabs.com/nexusguard/index.html",
    desc: "Computer-lab security across 1,000+ Windows PCs — remote cleanup, policy enforcement, fleet monitoring." },
  { name: "Auditorium & Events", tag: "Campus events", url: "https://events.fortifyhub.net",
    desc: "Hall booking, event publishing, QR check-in and certificates — built for BMSIT&M and rolling out campus-wide." },
  { name: "Solution — Service Desk", tag: "IT ticketing", url: "https://fortifyhub.net",
    desc: "Multi-tenant IT service desk for institutions — auto-routes tickets by category, SLA timers, per-institute branding." },
];

// Security pillars
export const SECURITY = [
  { icon: Lock,     label: "Encrypted secrets",
    desc: "Mail keys & passwords stored AES-encrypted, never in the browser.",
    detail: "Every credential configured — mail keys, provider tokens, passwords — is encrypted at rest with AES and is never sent to or stored in the browser." },
  { icon: ShieldCheck, label: "Tenant isolation",
    desc: "Every query scoped to its institute. No campus sees another's data.",
    detail: "Every database query is scoped to its tenant at the data-access layer, not just in the UI — so a bug in one institute's view can't leak another institute's data." },
  { icon: KeyRound, label: "Passwordless OTP",
    desc: "One-time email codes for staff and students. Admins may opt into password + OTP.",
    detail: "No password resets, no help-desk tickets about the help desk. A one-time code to the institute email is enough; admins can opt into password + OTP for elevated workflows." },
  { icon: Server,   label: "Edge-hosted",
    desc: "Runs on Cloudflare's global edge with D1 + Cron — fast and resilient.",
    detail: "The app runs on Cloudflare's global edge network with D1 as the backing store and a hourly cron for scheduled 24h-before-event reminder emails." },
  { icon: Globe,    label: "Data in India",
    desc: "DPDP-aligned data residency, built India-first by Arventiq Labs.",
    detail: "Data residency follows India's DPDP Act 2023 from the ground up — this isn't a retrofit, it's how Arventiq Labs builds every product." },
  { icon: MapPin,   label: "@bmsit.in allowed",
    desc: "Production checks the ALLOWED_EMAIL_DOMAIN. Outside emails are refused.",
    detail: "Every OTP request and registration checks the institute's email domain. The Worker rejects anything outside the allowlist at the very first call." },
];

export { MapPin };
